import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { FALLBACK_IMG } from "@/lib/api";
import Seo from "@/components/Seo";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, ArrowRight, ArrowUpRight } from "lucide-react";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

function PostCard({ post, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      data-testid={`blog-card-${post.slug}`}
    >
      <Link to={`/blog/${post.slug}`} className="group flex flex-col h-full border border-border bg-card/40 rounded-sm overflow-hidden hover:border-primary/60 transition-colors">
        <div className="relative aspect-[16/10] overflow-hidden bg-muted">
          <img
            src={post.featured_image_url || FALLBACK_IMG}
            alt={post.title}
            onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute top-3 left-3 font-mono-label text-[10px] px-2 py-1 bg-background/85 backdrop-blur text-primary border border-primary/30">
            {post.category}
          </span>
        </div>
        <div className="flex flex-col flex-1 p-6">
          <p className="font-mono-label text-[10px] text-muted-foreground mb-3">{fmtDate(post.published_at)}</p>
          <h3 className="font-display text-xl font-light leading-snug mb-3 group-hover:text-primary transition-colors">{post.title}</h3>
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 flex-1">{post.excerpt}</p>
          <span className="mt-5 inline-flex items-center gap-1.5 font-mono-label text-[11px] text-primary">
            Read Guide <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/posts").then((r) => {
      setPosts(r.data.posts || []);
      setCategories(r.data.categories || []);
    }).finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      if (active !== "All" && p.category !== active) return false;
      if (q && !`${p.title} ${p.excerpt}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [posts, active, query]);

  const tabs = ["All", ...categories];

  return (
    <div className="min-h-screen pt-28 pb-24 max-w-7xl mx-auto px-5 sm:px-8">
      <Seo title="Blog" path="/blog" description="Mushroom cultivation guides, substrate tips, and growing know-how from the NC Mycology lab." />

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <p className="font-mono-label text-xs text-primary mb-4">The Grow Journal</p>
        <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight max-w-3xl">
          Guides, tips &amp; <span className="text-primary italic">mycelium science.</span>
        </h1>
        <p className="mt-6 text-muted-foreground max-w-xl leading-relaxed">
          Everything we know about clean cultures, substrates, and contamination-free grows — straight from the lab.
        </p>
      </motion.div>

      {/* search */}
      <div className="relative mt-10 mb-6 max-w-md">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <input
          type="text" value={query} onChange={(e) => setQuery(e.target.value)} data-testid="blog-search-input"
          placeholder="Search articles…"
          className="w-full bg-card border border-border rounded-full pl-11 pr-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
        />
      </div>

      {/* category filters */}
      <div className="flex flex-wrap gap-2 mb-12 border-b border-border pb-6">
        {tabs.map((c) => (
          <button
            key={c} onClick={() => setActive(c)} data-testid={`blog-filter-${c}`}
            className={`font-mono-label text-[11px] px-4 py-2 rounded-full border transition-colors ${
              active === c ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary hover:text-primary"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-80 rounded-sm" />)}
        </div>
      ) : visible.length === 0 ? (
        <p className="text-muted-foreground py-20 text-center" data-testid="blog-empty">
          {query || active !== "All" ? "No articles match your filters yet." : "No articles published yet — check back soon."}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {visible.slice(0, 3).map((p, i) => <PostCard key={p.slug} post={p} index={i} />)}
          </div>

          {/* mid-page CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="my-14 relative overflow-hidden border border-primary/30 bg-primary/[0.05] rounded-sm p-8 lg:p-10 flex flex-col md:flex-row md:items-center gap-6"
            data-testid="blog-cta-banner"
          >
            <div className="flex-1">
              <p className="font-mono-label text-[10px] text-primary mb-2">Ready to grow?</p>
              <h2 className="font-display text-2xl lg:text-3xl font-light">Start with a clean liquid culture</h2>
              <p className="text-sm text-muted-foreground mt-2 max-w-md">Lab-tested syringes, ready to inoculate. Free the guesswork — start from the source.</p>
            </div>
            <Link to="/shop?category=Liquid+Cultures" data-testid="blog-cta-shop"
              className="shrink-0 inline-flex items-center gap-2 bg-primary hover:brightness-110 text-primary-foreground font-mono-label text-xs px-7 py-4 rounded-full transition-[filter] whitespace-nowrap">
              Shop Liquid Cultures <ArrowUpRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {visible.length > 3 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {visible.slice(3).map((p, i) => <PostCard key={p.slug} post={p} index={i + 3} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
